import { CommonModule } from '@angular/common';
import { afterNextRender, ChangeDetectorRef, Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  heroBanknotes,
  heroBuildingOffice,
  heroCalendarDays,
  heroCheckCircle,
  heroCircleStack,
  heroComputerDesktop,
  heroCreditCard,
  heroDocumentText,
  heroHashtag,
  heroIdentification,
  heroReceiptPercent,
  heroServerStack,
  heroShieldCheck,
  heroTag,
  heroUser,
  heroWallet,
  heroXCircle
} from '@ng-icons/heroicons/outline';
import { finalize } from 'rxjs';
import { PageResponse, TransactionApi, TransactionFilterValue, TransactionRow } from '../../core/transaction-api';
import { PosInstallmentsPlan, TerminalManagementApi } from '../../core/terminal-management-api';
import {
  acquirerOptions,
  brandOptions,
  cardTypeOptions,
  contextOptions,
  entryModeOptions,
  originOptions,
  processTypeOptions,
  paymentMethodOptions,
  statusOptions
} from '../../shared/options';
import { brandImage, dateTime, moneyFromCents, statusClass, text } from '../../shared/formatters';

type DetailTabId = 'operation' | 'payment' | 'card' | 'settlement' | 'plan' | 'system';

type DetailField = {
  key: string;
  label: string;
  icon: string;
};

type DetailTab = {
  id: DetailTabId;
  label: string;
  icon: string;
  fields: DetailField[];
};

@Component({
  selector: 'app-transactions',
  imports: [CommonModule, FormsModule, NgIcon],
  templateUrl: './transactions.html',
  providers: [provideIcons({
    heroBanknotes,
    heroBuildingOffice,
    heroCalendarDays,
    heroCheckCircle,
    heroCircleStack,
    heroComputerDesktop,
    heroCreditCard,
    heroDocumentText,
    heroHashtag,
    heroIdentification,
    heroReceiptPercent,
    heroServerStack,
    heroShieldCheck,
    heroTag,
    heroUser,
    heroWallet,
    heroXCircle
  })]
})
export class Transactions {
  readonly acquirers = acquirerOptions;
  readonly statuses = statusOptions;
  readonly brands = brandOptions;
  readonly cardTypes = cardTypeOptions;
  readonly entryModes = entryModeOptions;
  readonly origins = originOptions;
  readonly contexts = contextOptions;
  readonly processTypes = processTypeOptions;
  readonly paymentMethods = paymentMethodOptions;
  readonly pageSizes = [10, 25, 50, 100];

  rows: TransactionRow[] = [];
  filters: Record<string, TransactionFilterValue> = {};
  page = 0;
  size = 10;
  total = 0;
  loading = false;
  error = '';
  detail?: Record<string, unknown>;
  detailLoading = false;
  planDetail?: PosInstallmentsPlan;
  planLoading = false;
  planError = '';
  activeDetailTab: DetailTabId = 'operation';
  filtersOpen = false;

  readonly detailTabs: DetailTab[] = [
    {
      id: 'operation',
      label: 'Operacion',
      icon: '#',
      fields: [
        { key: 'id', label: 'ID', icon: '#' },
        { key: 'transactionId', label: 'Transaccion', icon: 'TX' },
        { key: 'solicitudPago', label: 'Solicitud pago', icon: 'SP' },
        { key: 'transactionDateTime', label: 'Fecha', icon: 'FE' },
        { key: 'transactionStatus', label: 'Estado', icon: 'ST' },
        { key: 'acquirer', label: 'Adquirente', icon: 'AD' },
        { key: 'transactionType', label: 'Contexto', icon: 'OP' },
        { key: 'paymentProcessType', label: 'Tipo proceso', icon: 'TP' },
        { key: 'transactionOriginType', label: 'Origen', icon: 'OR' },
        { key: 'mac', label: 'MAC', icon: 'MA' },
        { key: 'posEntryMode', label: 'Entry mode', icon: 'EM' },
        { key: 'responseCode', label: 'Respuesta', icon: 'RC' }
      ]
    },
    {
      id: 'payment',
      label: 'Pago',
      icon: '$',
      fields: [
        { key: 'amountTransaction', label: 'Monto', icon: '$' },
        { key: 'currency', label: 'Moneda', icon: 'MO' },
        { key: 'paymentMethod', label: 'Metodo', icon: 'PM' },
        { key: 'installment', label: 'Cuotas', icon: 'CU' },
        { key: 'authorizationCode', label: 'Autorizacion', icon: 'AU' },
        { key: 'processorId', label: 'Procesador', icon: 'PR' },
        { key: 'coelsaId', label: 'Coelsa', icon: 'CO' },
        { key: 'pixId', label: 'PIX', icon: 'PX' },
        { key: 'rrn', label: 'RRN', icon: 'RR' },
        { key: 'ticket', label: 'Ticket', icon: 'TK' }
      ]
    },
    {
      id: 'card',
      label: 'Tarjeta',
      icon: 'CC',
      fields: [
        { key: 'cardBrand', label: 'Marca', icon: 'BR' },
        { key: 'cardType', label: 'Card Type', icon: 'CT' },
        { key: 'card', label: 'PAN', icon: 'CC' },
        { key: 'cardHolderName', label: 'Titular', icon: 'TH' },
        { key: 'terminalId', label: 'Terminal', icon: 'TE' },
        { key: 'posId', label: 'POS', icon: 'PO' },
        { key: 'acquirerTerminalId', label: 'Terminal adquirente', icon: 'AT' }
      ]
    },
    {
      id: 'settlement',
      label: 'Batch / Wallet',
      icon: 'BW',
      fields: [
        { key: 'batchDetailId', label: 'Batch detail ID', icon: 'BD' },
        { key: 'batchNumber', label: 'Batch', icon: 'BA' },
        { key: 'acquirerBatchTerminalId', label: 'Terminal batch', icon: 'TB' },
        { key: 'batchBrand', label: 'Marca batch', icon: 'MB' },
        { key: 'walletAccountNumber', label: 'Cuenta wallet', icon: 'WA' },
        { key: 'walletAccountName', label: 'Wallet', icon: 'WL' }
      ]
    },
    {
      id: 'plan',
      label: 'Plan',
      icon: 'PL',
      fields: [
        { key: 'description', label: 'Descripcion', icon: 'DE' },
        { key: 'start', label: 'Inicio', icon: 'CR' },
        { key: 'ending', label: 'Fin', icon: 'UP' },
        { key: 'enabled', label: 'Estado', icon: 'ST' },
        { key: 'internacional', label: 'Internacional', icon: 'PL' },
        { key: 'clientFinancialCharge', label: 'Cargo cliente', icon: '$' },
        { key: 'surchargePercentage', label: 'Recargo', icon: 'RC' },
        { key: 'maxFinancingAmount', label: 'Max financiacion', icon: '$' },
        { key: 'binList', label: 'BINs', icon: 'CC' },
        { key: 'dayList', label: 'Dias', icon: 'FE' }
      ]
    },
    {
      id: 'system',
      label: 'Sistema',
      icon: 'S',
      fields: [
        { key: 'description', label: 'Descripcion', icon: 'DE' },
        { key: 'aip', label: 'AIP', icon: 'AI' },
        { key: 'softwareVersion', label: 'Software', icon: 'SW' },
        { key: 'isVoided', label: 'Anulada', icon: 'AN' },
        { key: 'isRefunded', label: 'Devuelta', icon: 'DV' },
        { key: 'createdAt', label: 'Creada', icon: 'CR' },
        { key: 'updatedAt', label: 'Actualizada', icon: 'UP' }
      ]
    }
  ];

  readonly text = text;
  readonly dateTime = dateTime;
  readonly moneyFromCents = moneyFromCents;
  readonly statusClass = statusClass;
  readonly brandImage = brandImage;
  readonly iconMap: Record<string, string> = {
    '#': 'heroHashtag',
    TX: 'heroIdentification',
    SP: 'heroReceiptPercent',
    FE: 'heroCalendarDays',
    ST: 'heroShieldCheck',
    AD: 'heroBuildingOffice',
    OP: 'heroTag',
    TP: 'heroServerStack',
    OR: 'heroCircleStack',
    EM: 'heroCreditCard',
    RC: 'heroCheckCircle',
    '$': 'heroBanknotes',
    MO: 'heroBanknotes',
    PM: 'heroCreditCard',
    AU: 'heroShieldCheck',
    PR: 'heroServerStack',
    CO: 'heroBuildingOffice',
    PX: 'heroWallet',
    RR: 'heroDocumentText',
    TK: 'heroReceiptPercent',
    CC: 'heroCreditCard',
    BR: 'heroTag',
    CT: 'heroCreditCard',
    TH: 'heroUser',
    ID: 'heroIdentification',
    TE: 'heroComputerDesktop',
    PO: 'heroComputerDesktop',
    AT: 'heroComputerDesktop',
    BW: 'heroCircleStack',
    BD: 'heroDocumentText',
    BA: 'heroCircleStack',
    TB: 'heroComputerDesktop',
    MB: 'heroTag',
    WA: 'heroWallet',
    WL: 'heroWallet',
    PL: 'heroDocumentText',
    S: 'heroServerStack',
    DE: 'heroDocumentText',
    AI: 'heroComputerDesktop',
    SW: 'heroServerStack',
    CU: 'heroReceiptPercent',
    AN: 'heroXCircle',
    DV: 'heroXCircle',
    CR: 'heroCalendarDays',
    UP: 'heroCalendarDays'
  };

  constructor(
    private readonly api: TransactionApi,
    private readonly managementApi: TerminalManagementApi,
    private readonly cdr: ChangeDetectorRef
  ) {
    afterNextRender(() => this.load());
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.total / this.size));
  }

  load(): void {
    this.loading = true;
    this.error = '';
    this.api
      .getHpgTransactions({ page: this.page, size: this.size, search: '', filters: this.cleanFilters() })
      .pipe(finalize(() => {
        this.loading = false;
        this.cdr.markForCheck();
      }))
      .subscribe({
        next: (response: PageResponse<TransactionRow>) => {
          this.rows = response.data ?? [];
          this.total = response.recordsFiltered ?? response.recordsTotal ?? 0;
          this.cdr.markForCheck();
        },
        error: error => {
          console.error('Error loading HPG transactions', error);
          this.error = 'No se pudieron cargar las transacciones HPG.';
          this.cdr.markForCheck();
        }
      });
  }

  applyFilters(): void {
    this.page = 0;
    this.load();
  }

  clearFilters(): void {
    this.filters = {};
    this.page = 0;
    this.load();
  }

  changePage(delta: number): void {
    if (this.loading) return;
    this.goToPage(this.page + delta);
  }

  goToPage(page: number): void {
    if (this.loading) return;
    const nextPage = Math.min(Math.max(page, 0), this.totalPages - 1);
    if (nextPage === this.page) return;
    this.page = nextPage;
    this.load();
  }

  visiblePages(): Array<number | 'ellipsis'> {
    const total = this.totalPages;
    const current = this.page;
    if (total <= 7) {
      return Array.from({ length: total }, (_, index) => index);
    }

    const pages = new Set<number>([0, total - 1]);
    for (let index = current - 1; index <= current + 1; index++) {
      if (index > 0 && index < total - 1) {
        pages.add(index);
      }
    }

    const sortedPages = Array.from(pages).sort((a, b) => a - b);
    return sortedPages.flatMap((item, index) => {
      const previous = sortedPages[index - 1];
      return index > 0 && item - previous > 1 ? ['ellipsis' as const, item] : [item];
    });
  }

  openDetail(id: number): void {
    this.detail = undefined;
    this.detailLoading = true;
    this.planDetail = undefined;
    this.planLoading = false;
    this.planError = '';
    this.activeDetailTab = 'operation';
    this.api
      .getHpgDetail(id)
      .pipe(finalize(() => {
        this.detailLoading = false;
        this.cdr.markForCheck();
      }))
      .subscribe({
        next: detail => {
          this.detail = detail;
          this.loadPlanDetail(detail['planId']);
          this.cdr.markForCheck();
        },
        error: () => {
          this.detail = { error: 'No se pudo cargar el detalle.' };
          this.cdr.markForCheck();
        }
      });
  }

  closeDetail(): void {
    this.detail = undefined;
    this.planDetail = undefined;
    this.planLoading = false;
    this.planError = '';
  }

  detailItems(tab: DetailTab): DetailField[] {
    if (!this.detail) return [];
    return tab.fields.filter(field => this.hasDetailValue(field.key));
  }

  detailValue(key: string): unknown {
    if (this.activeDetailTab === 'plan') {
      return this.planDetail?.[key as keyof PosInstallmentsPlan];
    }
    return this.detail?.[key];
  }

  formatDetailValue(key: string): string {
    const value = this.detailValue(key);
    if (this.activeDetailTab === 'plan') {
      if (key === 'enabled') return value ? 'Habilitado' : 'Deshabilitado';
      if (key === 'internacional' || key === 'clientFinancialCharge') return value ? 'Si' : 'No';
      if (Array.isArray(value)) return value.length ? value.join(', ') : '-';
    }
    if (key === 'amountTransaction') return moneyFromCents(value);
    if (key.toLowerCase().includes('date') || key === 'createdAt' || key === 'updatedAt') return dateTime(value);
    return text(value);
  }

  detailIcon(icon: string): string {
    return this.iconMap[icon] ?? 'heroTag';
  }

  activeDetailItems(): DetailField[] {
    const tab = this.detailTabs.find(item => item.id === this.activeDetailTab);
    return tab ? this.detailItems(tab) : [];
  }

  hasAssociatedPlan(): boolean {
    const planId = Number(this.detail?.['planId']);
    return Number.isFinite(planId) && planId > 0;
  }

  private hasDetailValue(key: string): boolean {
    const value = this.detailValue(key);
    return value !== null && value !== undefined && value !== '';
  }

  private loadPlanDetail(planId: unknown): void {
    const id = Number(planId);
    if (!Number.isFinite(id) || id <= 0) return;

    this.planLoading = true;
    this.managementApi.getInstallmentsPlan(id)
      .pipe(finalize(() => {
        this.planLoading = false;
        this.cdr.markForCheck();
      }))
      .subscribe({
        next: plan => {
          this.planDetail = plan;
          this.cdr.markForCheck();
        },
        error: () => {
          this.planError = `No se pudo cargar el plan ${id}.`;
          this.cdr.markForCheck();
        }
      });
  }

  private cleanFilters(): Record<string, TransactionFilterValue> {
    return Object.fromEntries(
      Object.entries(this.filters).filter(([, value]) =>
        Array.isArray(value) ? value.length > 0 : value !== undefined && value !== null && value !== ''
      )
    );
  }
}
